//src/hooks/useWorkerGroups.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addWorkerGroupMembers,
  createWorkerGroup,
  getWorkerGroup,
  listWorkerGroups,
  refreshWorkerGroup,
  removeWorkerGroupMember,
  setWorkerGroupStatus,
  updateWorkerGroup,
} from "@/services/workerGroups";
import type {
  WorkerGroupListParams,
  WorkerGroupMemberAddIn,
  WorkerGroupUpsertIn,
} from "@/types/gro";
import { useSelectedClientCompany } from "@/hooks/useSelectedClientCompany";

export const WORKER_GROUPS_QK = (clientCompanyId: string | null, params: WorkerGroupListParams) =>
  ["worker_groups", clientCompanyId, params] as const;

export function useWorkerGroups(params: WorkerGroupListParams) {
  const { selectedClientCompanyId } = useSelectedClientCompany();

  return useQuery({
    queryKey: WORKER_GROUPS_QK(selectedClientCompanyId, params),
    queryFn: () => listWorkerGroups(params),
    enabled: !!selectedClientCompanyId,
    staleTime: 20_000,
  });
}

export function useWorkerGroupDetail(id: string | undefined) {
  const { selectedClientCompanyId } = useSelectedClientCompany();

  return useQuery({
    queryKey: ["worker_group_detail", selectedClientCompanyId, id],
    queryFn: () => getWorkerGroup(id as string),
    enabled: !!id && !!selectedClientCompanyId,
    staleTime: 20_000,
  });
}

export function useCreateWorkerGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: WorkerGroupUpsertIn) => createWorkerGroup(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
    },
  });
}

export function useUpdateWorkerGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkerGroupUpsertIn }) =>
      updateWorkerGroup(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}

export function useSetWorkerGroupStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      setWorkerGroupStatus(id, { is_active }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}

export function useRefreshWorkerGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => refreshWorkerGroup(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", id] });
    },
  });
}

export function useAddWorkerGroupMembers() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: WorkerGroupMemberAddIn }) =>
      addWorkerGroupMembers(id, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}

export function useRemoveWorkerGroupMember() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      removeWorkerGroupMember(id, userId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["worker_groups"] });
      qc.invalidateQueries({ queryKey: ["worker_group_detail", variables.id] });
    },
  });
}
